/**
 * HOF-696 D5 — the shell of a self-only labour seat (a farm worker).
 *
 * Logo, language picker, log-out and the labour content — nothing else. A worker
 * seat holds no `farm.read` and no `assistant.use`, so the normal shell's
 * ChatProvider, AppSearchController, focus rail, settings and integrations would
 * each be a door to a 403; they are not mounted at all, which also keeps the
 * phone's first load light. The page body is the Morgenlage mobile shell.
 */
import React from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import ImpersonationBanner from '../components/ImpersonationBanner';
import { LANGUAGE_OPTIONS } from '../utils/language';
import './WorkerLayout.css';

const WorkerLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t, i18n } = useTranslation();
  const { logout, impersonation, stopImpersonation, userProfile } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await logout();
      showToast(t('labour.shell.loggedOut'), 'success');
    } finally {
      navigate('/login');
    }
  };

  const language = (i18n.language || 'de').split('-')[0];

  return (
    <div className="worker-layout mgl-root" data-testid="worker-layout">
      {impersonation?.active && (
        <ImpersonationBanner farmName={impersonation.farm_name} userId={impersonation.user_id} onExit={stopImpersonation} />
      )}
      <header className="worker-layout__header">
        <img className="worker-layout__logo" src="/hofsync_logo.gif" alt="HofSync" />
        <span className="worker-layout__farm">{userProfile?.active_farm?.farm_name ?? ''}</span>
        <select
          className="worker-layout__lang"
          aria-label={t('labour.shell.language')}
          value={language}
          onChange={(e) => i18n.changeLanguage(e.target.value)}
        >
          {LANGUAGE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <button type="button" className="worker-layout__logout" onClick={handleLogout}>
          {t('labour.shell.logout')}
        </button>
      </header>
      <main className="mgl-screen worker-layout__main">{children}</main>
    </div>
  );
};

export default WorkerLayout;
